import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FileText, Smile, Plus } from "lucide-react";
import { toast } from "sonner";

const MOOD = {
  happy: { label: "سعيد", color: "bg-emerald-100 text-emerald-700" },
  calm: { label: "هادئ", color: "bg-sky-100 text-sky-700" },
  anxious: { label: "قلق", color: "bg-amber-100 text-amber-700" },
  tired: { label: "متعب", color: "bg-slate-100 text-slate-700" },
  upset: { label: "منزعج", color: "bg-rose-100 text-rose-700" },
};

const empty = { child_id: "", date: new Date().toISOString().slice(0, 10), mood: "happy", behavior: "", skills: "", food_intake: "", notes: "" };

export default function DailyReportsPage() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [children, setChildren] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const canCreate = user.role === "teacher" || user.role === "admin";

  const load = async () => {
    const [{ data: r }, { data: c }] = await Promise.all([
      api.get("/daily-reports"),
      api.get("/children"),
    ]);
    setReports(r);
    setChildren(c);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.child_id) { toast.error("اختر الطفل"); return; }
    try {
      await api.post("/daily-reports", form);
      toast.success("تم إرسال التقرير — إشعار للأهل عبر الواتساب (محاكاة)");
      setOpen(false);
      setForm(empty);
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900">التقارير اليومية</h1>
          <p className="text-slate-500 mt-2">تسجيل يوم الطفل ومشاركته مع ولي الأمر</p>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="h-12 gap-2 bg-sky-600 hover:bg-sky-700" data-testid="new-report-btn">
                <Plus className="w-4 h-4" /> تقرير جديد
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="text-2xl font-black">تقرير يومي</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-1.5">
                  <Label className="font-bold">الطفل</Label>
                  <Select value={form.child_id} onValueChange={(v) => setForm({ ...form, child_id: v })}>
                    <SelectTrigger data-testid="report-child"><SelectValue placeholder="اختر الطفل" /></SelectTrigger>
                    <SelectContent>{children.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="font-bold">التاريخ</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="font-bold">المزاج</Label>
                  <Select value={form.mood} onValueChange={(v) => setForm({ ...form, mood: v })}>
                    <SelectTrigger data-testid="report-mood"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(MOOD).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="font-bold">السلوك</Label>
                  <Textarea data-testid="report-behavior" value={form.behavior} onChange={(e) => setForm({ ...form, behavior: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="font-bold">المهارات المكتسبة</Label>
                  <Textarea data-testid="report-skills" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="font-bold">الطعام</Label>
                  <Input value={form.food_intake} onChange={(e) => setForm({ ...form, food_intake: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="font-bold">ملاحظات</Label>
                  <Textarea data-testid="report-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
                <Button data-testid="save-report-btn" onClick={save} className="w-full h-12 bg-sky-600 hover:bg-sky-700">إرسال التقرير</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {reports.map((r) => (
          <Card key={r.id} data-testid={`report-${r.id}`} className="border-slate-200/70 card-elevated">
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-black text-slate-900">{r.child_name}</div>
                    <div className="text-xs text-slate-500">{r.date} · {r.author_name}</div>
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-full text-xs font-bold ${MOOD[r.mood]?.color}`}>
                  <Smile className="inline w-3 h-3 mr-1" /> {MOOD[r.mood]?.label}
                </div>
              </div>
              {r.behavior && <ReportRow label="السلوك" value={r.behavior} />}
              {r.skills && <ReportRow label="المهارات" value={r.skills} />}
              {r.notes && <ReportRow label="ملاحظات" value={r.notes} />}
            </CardContent>
          </Card>
        ))}
        {reports.length === 0 && <Card className="md:col-span-2"><CardContent className="p-16 text-center text-slate-400">لا توجد تقارير</CardContent></Card>}
      </div>
    </div>
  );
}

function ReportRow({ label, value }) {
  return (
    <div className="mt-2 text-sm">
      <span className="font-bold text-slate-700">{label}: </span>
      <span className="text-slate-600">{value}</span>
    </div>
  );
}
